/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { Divider, Grid, PageGridContainer, Typography, Typography2 } from '@coursera/cds-core';

import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import type { Props as ConfigRendererProps } from 'bundles/page-config-common/components/ConsumerComponentsConfigRenderer';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import HeaderLevelProvider from 'bundles/page-config-common/providers/HeaderLevelOrderProvider';
import type { PageConfig_GroupedComponentsWrapperMetadataFragment as GroupedComponentsWrapper } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';

type Props = {
  pageConfigComponent: GroupedComponentsWrapper;
  ConfigRenderer: ({ pageConfigComponents }: ConfigRendererProps) => JSX.Element | null;
};

const styles = {
  // Make margins between nested components smaller to display better grouping
  wrapper: css`
    .HomeSection {
      margin: 0 auto;
    }
  `,
  // Do not render the title if there is no nested components rendered
  groupedComponentsWrapper: css`
    :has(.ConfigRendererWrapper:empty) {
      display: none;
    }
  `,
};

const GroupedComponentsWrapperRenderer = ({ pageConfigComponent, ConfigRenderer }: Props) => {
  const { currentLevel, nextLevel } = useHeaderLevel();

  const { components, title, body } = pageConfigComponent;

  const enableEnterpriseHomeRefresh = GrowthDiscoveryEpicClient.preview('enableEnterpriseHomeRefresh');
  // We want to not allow more than 1 level of nesting for grouped components
  // Thus, we filter out any nested GroupedComponentsWrapper components. This is also done in the BE.
  const nestedComponents = useMemo(
    () => components.filter((component) => component?.__typename !== 'PageConfig_GroupedComponentsWrapper'),
    [components]
  );

  return (
    <div css={styles.groupedComponentsWrapper}>
      <HomeSection className="PageConfig_GroupedComponentsWrapper">
        {(title || body) && (
          <PageGridContainer>
            <Grid item xs={12}>
              {title && (
                <Typography2 component={currentLevel ?? 'h2'} variant="titleMedium">
                  {title}
                </Typography2>
              )}
              {body && (
                <Typography component="p" variant="body1">
                  {body}
                </Typography>
              )}
            </Grid>
          </PageGridContainer>
        )}
        <HeaderLevelProvider initialLevel={nextLevel}>
          <div css={styles.wrapper} className="ConfigRendererWrapper">
            <ConfigRenderer pageConfigComponents={nestedComponents} disablePagination={true} />
          </div>
        </HeaderLevelProvider>
      </HomeSection>
      {enableEnterpriseHomeRefresh && (
        <PageGridContainer>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </PageGridContainer>
      )}
    </div>
  );
};

export default GroupedComponentsWrapperRenderer;
